import { Currency, CurrencySymbol } from './currency';
import {
  DistanceUnit,
  LocalizedDateTimeFormatKey,
} from './localization-setting';

import {
  DEFAULT_LANGUAGE_TYPE,
  LangType,
  SHOW_TRANSLATION_KEY,
  SUPPORTED_LANGUAGE_TYPES,
} from '@wilson/localization/constants';

import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import fr from '@angular/common/locales/fr';
import pl from '@angular/common/locales/pl';

// Moved to @wilson/localization/constants so framework-free libs can use them.
// Re-exported to keep the public API of @wilson/localization unchanged.
export {
  DEFAULT_LANGUAGE_TYPE,
  SHOW_TRANSLATION_KEY,
  SUPPORTED_LANGUAGE_TYPES,
};
export type { LangType };

type AngularLocaleData = typeof en;

export const DEFAULT_DISTANCE_UNIT: DistanceUnit = 'km';
export const DEFAULT_CURRENCY: Currency = Currency.EUR;
export const DEFAULT_CURRENCY_SYMBOL: CurrencySymbol = CurrencySymbol.EUR;
export const DEFAULT_TIMEZONE = 'Europe/Berlin';

export const DEFAULT_DATE_FORMATS: Record<LocalizedDateTimeFormatKey, string> =
  {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    dateTimeFormat: 'dd/MM/yyyy, HH:mm',
    dayMonthFormat: 'dd/MM',
    monthYearFormat: 'MMMM yyyy',
  };

export const PORTAL_UI_LANGUAGE_OPTIONS: Record<
  Exclude<LangType, typeof SHOW_TRANSLATION_KEY>,
  string
> = {
  'de-DE': 'Deutsch',
  'en-GB': 'English (UK)',
  'fr-FR': 'Français',
  'pl-PL': 'Polski',
};

export const ANGULAR_LOCALE_DATA: Record<
  Exclude<LangType, typeof SHOW_TRANSLATION_KEY>,
  AngularLocaleData
> = {
  'de-DE': de,
  'en-GB': en,
  'fr-FR': fr,
  'pl-PL': pl,
};

/** Real app locales (excludes dev-only SHOW_TRANSLATION_KEY). */
export type MobileUiLangType = Exclude<LangType, typeof SHOW_TRANSLATION_KEY>;

/**
 * Locales offered in the mobile language picker.
 * Keep `settings.languages.settingsOptions.<code>` in mobile i18n in sync.
 */
export const MOBILE_UI_LANGUAGE_TYPES = [
  'de-DE',
  'en-GB',
  'fr-FR',
  'pl-PL',
] as const satisfies readonly MobileUiLangType[];

/** Maps legacy stored locale codes (e.g. before en-US was removed from pickers) to a valid {@link LangType}. */
export function normalizeLanguagePreference(
  raw: string | null | undefined,
): LangType | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (raw === 'en-US') {
    return 'en-GB';
  }
  if ((SUPPORTED_LANGUAGE_TYPES as readonly string[]).includes(raw)) {
    return raw as LangType;
  }
  return null;
}

type ExtractLanguageCode<T> =
  T extends `${infer primaryLanguageSubtag}-${infer _regionSubtag}`
    ? Lowercase<primaryLanguageSubtag>
    : never;

export type BackendLangType = ExtractLanguageCode<
  Exclude<LangType, typeof SHOW_TRANSLATION_KEY>
>;
export type EntityLangType = `name${Capitalize<BackendLangType>}`;
